import { InfiniteSlider } from '@/components/ui/infinite-slider';
import { ProgressiveBlur } from '@/components/ui/progressive-blur';
import { Glow } from '@/components/ui/glow';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { clientLogos } from '@/data/clientLogos';
import fusionLogo from '@/assets/fusion-logo.png';
import { FusionLayersDiagram } from '@/components/ui/fusion-layers-diagram';
import { navigateToEnterpriseDemo } from '@/utils/navigation';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

export function HeroSection() {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const handleClick = () => navigate('/customer-stories');
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };
  return <main className="overflow-x-hidden">
            <section className="relative overflow-hidden bg-gradient-to-br from-background via-background to-primary/5">
                {/* Enhanced Glow Effects */}
                <Glow variant="above" className="opacity-20" />
                <Glow variant="center" className="opacity-10" />
                
                <div className="pb-4 pt-14 md:pb-6 md:pt-20 lg:pb-6 lg:pt-24 relative overflow-visible">
                    {/* Enhanced floating shapes background */}
                    <div className="absolute inset-0 pointer-events-none overflow-hidden">
                        <motion.div className="absolute top-16 left-[20%] w-32 h-32 bg-muted/20 rounded-full" animate={{
            scale: [1, 1.1, 1],
            opacity: [0.2, 0.4, 0.2],
            rotate: [0, 180, 360]
          }} transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }} />
                        <motion.div className="absolute top-32 right-[20%] w-20 h-20 bg-accent/15 rotate-45" animate={{
            y: [0, -20, 0],
            rotate: [45, 225, 45],
            opacity: [0.4, 0.8, 0.4]
          }} transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }} />
                        <motion.div className="absolute bottom-32 left-[15%] w-24 h-40 bg-secondary/12 rounded-lg" animate={{
            x: [0, 15, 0],
            scale: [1, 1.05, 1],
            opacity: [0.3, 0.7, 0.3]
          }} transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 3
          }} />
                        <motion.div className="absolute top-1/3 right-[30%] w-16 h-12 bg-accent/15 rounded-full" animate={{
            scale: [1, 1.15, 1],
            opacity: [0.2, 0.4, 0.2]
          }} transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }} />
                    </div>
                    
                    <div className="mx-auto max-w-7xl px-6 relative z-0">
                        {/* Logo */}
                        <motion.img src={fusionLogo} alt="gimmefy fusion - Humans + AI. Unfair Together." className="w-full max-w-[108px] sm:max-w-[132px] lg:max-w-[156px] mx-auto" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            ease: [0.16, 1, 0.3, 1]
          }} />
                        
                        {/* H1 Headline - Full Width */}
                        <motion.h1 className="mt-6 text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight text-center" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            ease: [0.16, 1, 0.3, 1],
            delay: 0.2
          }}>
                            The AI Content System that helps modern marketing teams unlock their{" "}
                            <span className="bg-gradient-primary bg-clip-text text-transparent">
                                unfair advantage
                            </span>.
                        </motion.h1>
                        
                        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-center lg:gap-16 mt-8">
                            {/* Text Content - Left Side */}
                            <div className="flex-1 lg:max-w-xl text-center lg:text-left lg:ml-12 lg:mt-4">
              <motion.div id="hero-sub-description" className="text-lg lg:text-xl leading-relaxed text-secondary-foreground" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.8,
                ease: [0.16, 1, 0.3, 1],
                delay: 0.4
              }}>
                <p>The best brand campaigns don't come from AI alone or endless hours by yourself.</p>
                <p className="mt-4">They come from <span className="bg-gradient-primary bg-clip-text text-transparent font-semibold">your creative vision and experience</span>, empowered by <span className="bg-gradient-primary bg-clip-text text-transparent font-semibold">AI's execution</span>, working together in <span className="bg-gradient-primary bg-clip-text text-transparent font-semibold">a unified system that truly understands you.</span></p>
              </motion.div>

                                <motion.div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center lg:justify-start" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.8,
                ease: [0.16, 1, 0.3, 1],
                delay: 0.6
              }}>
                                    <Button id="hero-cta-book-session" variant="hero" size="lg" className="h-12 gap-2 shadow-2xl shadow-primary/25 hover:shadow-primary/40 transform hover:scale-105 transition-all duration-300" onClick={() => navigateToEnterpriseDemo()}>
                                        Book a Working Session
                                    </Button>
                                </motion.div>
                            </div>

                            {/* Animated FUSION Diagram - Right Side - Moved Down */}
                            <motion.div className="flex-1 mt-8 lg:mt-4 w-full lg:max-w-[45%]" initial={{
              opacity: 0,
              x: 30
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 1,
              ease: [0.16, 1, 0.3, 1],
              delay: 0.8
            }}>
                                <div className="relative group">
                                    {/* Glow effects around diagram */}
                                    <div className="absolute -inset-8 bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-emerald-500/10 rounded-full blur-2xl opacity-60 group-hover:opacity-80 transition-opacity duration-500" />
                                    
                                    <FusionLayersDiagram />
                                    
                                    
                                     {/* Floating elements around video */}
                                    <motion.div className="absolute -top-4 -right-4 w-12 h-12 bg-primary/20 rounded-full" animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.4, 0.8, 0.4]
                }} transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }} />
                                    <motion.div className="absolute -bottom-2 -left-2 w-8 h-8 bg-accent/30 rounded-lg rotate-45" animate={{
                  rotate: [45, 225, 45],
                  scale: [1, 1.05, 1]
                }} transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1
                }} />
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            
            <div 
              className="mt-10 md:mt-14 lg:mt-16 pb-2 md:pb-3 cursor-pointer hover:bg-white/5 transition-colors"
              onClick={handleClick} 
              onKeyDown={handleKeyDown} 
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              role="link" 
              tabIndex={0}
            >
              <div className="group relative m-auto max-w-6xl px-6">
                <div className="flex flex-col items-center md:flex-row">
                  <div className="md:max-w-44 md:border-r md:pr-6">
                    <p className="text-end text-sm">
                      The AI behind <span className="hidden md:block"></span>
                      <span className="md:hidden">human teams</span>
                      <span className="hidden md:inline">human teams</span>
                    </p>
                  </div>
                  <div className="relative py-4 md:w-[calc(100%-11rem)] overflow-hidden">
                    <InfiniteSlider duration={40} gap={64}>
                      {clientLogos.map((logo, index) => (
                        <div key={`logo-${index}`} className="flex">
                          <img 
                            className="mx-auto h-8 w-fit opacity-80 hover:opacity-100 transition-all duration-300 filter grayscale hover:grayscale-0" 
                            src={logo} 
                            alt={`Client logo ${index + 1}`} 
                            height="32" 
                            width="100" 
                            loading="eager" 
                            onError={e => {
                              const target = e.currentTarget;
                              target.style.display = 'none';
                            }} 
                          />
                        </div>
                      ))}
                    </InfiniteSlider>

                    <ProgressiveBlur className="pointer-events-none absolute left-0 top-0 h-full w-20" direction="left" blurIntensity={1} />
                    <ProgressiveBlur className="pointer-events-none absolute right-0 top-0 h-full w-20" direction="right" blurIntensity={1} />
                    
                    {/* Hover overlay button - desktop only */}
                    {isHovered && (
                      <div className="hidden lg:flex absolute inset-0 items-center justify-center bg-background/80 backdrop-blur-sm z-40">
                        <span className="bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium shadow-lg">
                          Read their stories →
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Permanent button - mobile only */}
                  <div className="flex lg:hidden justify-center mt-4">
                    <span className="bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium shadow-lg">
                      Read their stories →
                    </span>
                  </div>
                </div>
              </div>
            </div>
            </section>
        </main>;
}